import { useState, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useDashboardStats, DateFilter } from "@/hooks/useDashboardStats";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { useVLETools, VLE_TOOLS } from "@/hooks/useVLETools";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AppLayout } from "@/components/layout/AppLayout";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { QuickSaleModal } from "@/components/dashboard/QuickSaleModal";
import { QuickExpenseModal } from "@/components/dashboard/QuickExpenseModal";
import { QuickReceivePaymentModal } from "@/components/dashboard/QuickReceivePaymentModal";
import { DailySummaryPopup } from "@/components/dashboard/DailySummaryPopup";
import { InvoicePreviewPopup } from "@/components/dashboard/InvoicePreviewPopup";
import { AddKioskTransactionModal } from "@/components/kiosk/AddKioskTransactionModal";
import { TrialWatermark } from "@/components/subscription/TrialWatermark";
import { TrialReminderPopup } from "@/components/subscription/TrialReminderPopup";
import { VLEAnalyticsDashboard } from "@/components/settings/VLEAnalyticsDashboard";
import { FastBillingBar } from "@/components/dashboard/FastBillingBar";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { 
  Plus, 
  Wallet, 
  Receipt,
  Users,
  Settings,
  BookOpen,
  Landmark,
  Send,
  FileBarChart,
  IndianRupee,
  TrendingUp,
  TrendingDown,
  Clock,
  ChevronRight,
  BarChart3,
  Fingerprint,
  ArrowUpRight,
  ArrowDownLeft,
  Banknote,
  Wifi
} from "lucide-react";
import { format } from "date-fns";

// LAZY LOAD: Heavy chart components for faster initial render
const LazySalesExpensesChart = lazy(() => import("@/components/dashboard/DashboardCharts").then(m => ({ default: m.SalesExpensesChart })));
const LazyCategoryPieChart = lazy(() => import("@/components/dashboard/DashboardCharts").then(m => ({ default: m.CategoryPieChart })));

// Chart skeleton for lazy loading
const ChartSkeleton = ({ height = 180 }: { height?: number }) => (
  <Skeleton className="w-full" style={{ height }} />
);

interface InvoiceData {
  id: string;
  invoiceNumber?: string;
  customerName?: string;
  customerPhone?: string;
  items: Array<{
    serviceName: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
  }>;
  totalAmount: number;
  receivedAmount: number;
  dueAmount: number;
  paymentMode: string;
  paymentStatus: string;
  createdAt: string;
}

export default function Dashboard() {
  const { t } = useLanguage();
  const { profile, tenant } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<DateFilter>('today');
  
  // Modal states
  const [saleModalOpen, setSaleModalOpen] = useState(false);
  const [expenseModalOpen, setExpenseModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
  const [lastInvoice, setLastInvoice] = useState<InvoiceData | null>(null);
  const [kioskModalOpen, setKioskModalOpen] = useState(false);
  
  // Enable keyboard shortcuts
  useKeyboardShortcuts();
  
  // Check if tools are enabled
  const { isToolEnabled } = useVLETools();
  const isKioskEnabled = isToolEnabled(VLE_TOOLS.KIOSK_BANKING);
  
  const { stats, chartData, categoryData, recentTransactions, isLoading } = useDashboardStats(filter);
  
  // Fetch today's kiosk transactions for summary - OPTIMIZED: Only fetch if kiosk enabled
  const todayStr = format(new Date(), "yyyy-MM-dd");
  const { data: todayKiosk } = useQuery({
    queryKey: ["dashboard-kiosk", tenant?.id, todayStr],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("kiosk_transactions")
        .select("*")
        .eq("tenant_id", tenant?.id)
        .eq("transaction_date", todayStr);
      if (error) throw error;
      return data;
    },
    enabled: !!tenant?.id && isKioskEnabled, // Skip query if kiosk not enabled
    staleTime: 2 * 60 * 1000, // 2 minutes cache
    gcTime: 5 * 60 * 1000,
  });

  // Calculate kiosk totals
  const kioskTotals = todayKiosk?.reduce(
    (acc, tx) => ({
      withdrawCount: acc.withdrawCount + (tx.transaction_type === "withdraw" ? 1 : 0),
      depositCount: acc.depositCount + (tx.transaction_type === "deposit" ? 1 : 0),
      withdrawAmount: acc.withdrawAmount + (tx.transaction_type === "withdraw" ? Number(tx.amount) : 0),
      depositAmount: acc.depositAmount + (tx.transaction_type === "deposit" ? Number(tx.amount) : 0),
      cashCommission: acc.cashCommission + Number(tx.manual_commission || 0),
      portalCommission: acc.portalCommission + Number(tx.portal_commission || 0),
      totalCount: acc.totalCount + 1,
    }),
    { withdrawCount: 0, depositCount: 0, withdrawAmount: 0, depositAmount: 0, cashCommission: 0, portalCommission: 0, totalCount: 0 }
  ) || { withdrawCount: 0, depositCount: 0, withdrawAmount: 0, depositAmount: 0, cashCommission: 0, portalCommission: 0, totalCount: 0 };
  
  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard-kiosk"] });
  };
  
  const handleInvoiceCreated = (invoice: InvoiceData) => {
    setLastInvoice(invoice);
    setInvoicePreviewOpen(true);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const filterOptions: { label: string; value: DateFilter }[] = [
    { label: t('today'), value: 'today' },
    { label: t('this_week'), value: 'week' },
    { label: t('this_month'), value: 'month' },
  ];

  // Check which tools are enabled for Daily Book
  const isDailyBookEnabled = isToolEnabled(VLE_TOOLS.DAILY_BOOK);
  
  const quickModules = [
    { title: 'Billing', icon: Receipt, path: '/sales', color: 'bg-primary/10 text-primary' },
    { title: 'Customers', icon: Users, path: '/customers', color: 'bg-blue-500/10 text-blue-600' },
    { title: 'Services', icon: Settings, path: '/services', color: 'bg-purple-500/10 text-purple-600' },
    ...(isDailyBookEnabled ? [{ title: 'Daily Book', icon: BookOpen, path: '/daily-book', color: 'bg-green-500/10 text-green-600' }] : []),
    ...(isKioskEnabled ? [{ title: 'Kiosk Book', icon: Landmark, path: '/kiosk-book', color: 'bg-amber-500/10 text-amber-600' }] : []),
    { title: 'WhatsApp', icon: Send, path: '/customer-notifications', color: 'bg-emerald-500/10 text-emerald-600' },
    { title: 'Reports', icon: FileBarChart, path: '/reports', color: 'bg-rose-500/10 text-rose-600' },
  ];

  const getPaymentStatusBadge = (tx: { amount: number; mode: string }) => {
    // Simple heuristic - if mode includes 'partial' or 'due', show differently
    const isPaid = tx.mode?.toLowerCase() !== 'due';
    return isPaid ? (
      <Badge variant="secondary" className="bg-green-500/10 text-green-700 dark:text-green-400 border-0 font-medium">
        Paid
      </Badge>
    ) : (
      <Badge variant="secondary" className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-0 font-medium">
        Pending
      </Badge>
    );
  };

  return (
    <AppLayout>
      {/* Trial Watermark - Shows in bottom right during trial */}
      <TrialWatermark />
      
      {/* Trial Reminder Popup - Smart 4-hour gap reminders */}
      <TrialReminderPopup />
      

      <div className="space-y-6">
        {/* Fast Billing Bar - Desktop Only */}
        <FastBillingBar onSuccess={handleRefresh} />
        
        {/* Top Quick Action Bar - Always Visible */}
        <div className="sticky top-0 z-10 -mx-4 -mt-4 px-4 py-3 bg-background/95 backdrop-blur border-b border-border">
          <div className="flex flex-wrap items-center gap-3">
            <Button 
              size="lg" 
              className="gap-2 shadow-md font-semibold h-12 px-6"
              onClick={() => setSaleModalOpen(true)}
            >
              <Plus className="h-5 w-5" />
              {t('add_new_sale')}
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="gap-2 h-12 px-5 border-2"
              onClick={() => setExpenseModalOpen(true)}
            >
              <Wallet className="h-5 w-5" />
              {t('add_new_expense')}
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="gap-2 h-12 px-5 border-2"
              onClick={() => setPaymentModalOpen(true)}
            >
              <IndianRupee className="h-5 w-5" />
              Receive Payment
            </Button>
            <Button
              size="lg" 
              variant="secondary" 
              className="gap-2 h-12 px-5"
              onClick={() => navigate('/reports')}
            >
              <FileBarChart className="h-5 w-5" />
              {t('reports')}
            </Button>
            {isKioskEnabled && (
              <Button
                size="lg" 
                variant="secondary" 
                className="gap-2 h-12 px-5 bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20 border border-amber-500/30"
                onClick={() => setKioskModalOpen(true)}
              >
                <Fingerprint className="h-5 w-5" />
                KIOSK
              </Button>
            )}
            <Button
              size="lg" 
              variant="outline" 
              className="gap-2 h-12 px-5 ml-auto"
              onClick={() => setSummaryModalOpen(true)}
            >
              <BarChart3 className="h-5 w-5" />
              Summary
            </Button>
          </div>
        </div>

        {/* Header with Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
          <div>
            <h1 className="text-xl font-bold tracking-tight">
              {t('dashboard')} — {profile?.full_name || 'Operator'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {format(new Date(), 'EEEE, dd MMMM yyyy')}
            </p>
          </div>
          <div className="flex gap-1 bg-muted/30 p-1 rounded-lg">
            {filterOptions.map((option) => (
              <Button
                key={option.value}
                variant={filter === option.value ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setFilter(option.value)}
                className={filter === option.value ? '' : 'text-muted-foreground'}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Today Summary - 4 Metric Cards */}
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {/* Sales Card */}
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('todays_sales')}</p>
                  {isLoading ? (
                    <Skeleton className="h-9 w-28 mt-1" />
                  ) : (
                    <p className="text-2xl lg:text-3xl font-bold text-foreground">
                      {formatCurrency(stats.totalSales)}
                    </p>
                  )}
                </div>
                <div className="h-12 w-12 rounded-full bg-green-500/10 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Expenses Card */}
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('todays_expenses')}</p>
                  {isLoading ? (
                    <Skeleton className="h-9 w-28 mt-1" />
                  ) : (
                    <p className="text-2xl lg:text-3xl font-bold text-foreground">
                      {formatCurrency(stats.totalExpenses)}
                    </p>
                  )}
                </div>
                <div className="h-12 w-12 rounded-full bg-red-500/10 flex items-center justify-center">
                  <TrendingDown className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pending Card */}
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('pending_payments')}</p>
                  {isLoading ? (
                    <Skeleton className="h-9 w-28 mt-1" />
                  ) : (
                    <p className="text-2xl lg:text-3xl font-bold text-foreground">
                      {formatCurrency(stats.pendingAmount)}
                    </p>
                  )}
                </div>
                <div className="h-12 w-12 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Profit Card */}
          <Card className="border-l-4 border-l-primary">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('total_profit')}</p>
                  {isLoading ? (
                    <Skeleton className="h-9 w-28 mt-1" />
                  ) : (
                    <p className={`text-2xl lg:text-3xl font-bold ${stats.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(stats.profit)}
                    </p>
                  )}
                </div>
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <IndianRupee className="h-6 w-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Kiosk Summary - Show if there are transactions today AND kiosk is enabled */}
        {isKioskEnabled && kioskTotals.totalCount > 0 && (
          <Card className="bg-gradient-to-r from-amber-500/5 to-orange-500/5 border-amber-500/20">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Landmark className="h-4 w-4 text-amber-600" />
                  Today's Kiosk Summary
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate('/kiosk-report')}
                  className="text-amber-600 gap-1"
                >
                  View Report
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pb-4">
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground mb-1">
                    <ArrowUpRight className="h-3 w-3 text-destructive" />
                    Withdrawals
                  </div>
                  <p className="text-lg font-bold">{kioskTotals.withdrawCount}</p>
                  <p className="text-xs text-muted-foreground">₹{kioskTotals.withdrawAmount.toLocaleString()}</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground mb-1">
                    <ArrowDownLeft className="h-3 w-3 text-green-600" />
                    Deposits
                  </div>
                  <p className="text-lg font-bold text-green-600">{kioskTotals.depositCount}</p>
                  <p className="text-xs text-muted-foreground">₹{kioskTotals.depositAmount.toLocaleString()}</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground mb-1">
                    <Banknote className="h-3 w-3 text-green-600" />
                    Cash Commission
                  </div>
                  <p className="text-lg font-bold text-green-600">₹{kioskTotals.cashCommission.toFixed(0)}</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground mb-1">
                    <Wifi className="h-3 w-3 text-blue-600" />
                    Portal Commission
                  </div>
                  <p className="text-lg font-bold text-blue-600">₹{kioskTotals.portalCommission.toFixed(0)}</p>
                </div>
                <div className="text-center col-span-2 md:col-span-2 bg-amber-500/10 rounded-lg py-2">
                  <p className="text-xs text-muted-foreground mb-1">Total Earned</p>
                  <p className="text-xl font-bold text-amber-600">
                    ₹{(kioskTotals.cashCommission + kioskTotals.portalCommission).toFixed(0)}
                  </p>
                  <p className="text-[10px] text-muted-foreground">{kioskTotals.totalCount} transactions</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Access Modules Grid */}
        <div>
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Quick Access
          </h2>
          <div className="grid grid-cols-4 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            {quickModules.map((module) => (
              <button
                key={module.path}
                onClick={() => navigate(module.path)}
                className="flex flex-col items-center justify-center p-4 rounded-xl bg-card hover:bg-accent/50 border border-border hover:border-primary/30 transition-colors group"
              >
                <div className={`h-12 w-12 rounded-xl ${module.color} flex items-center justify-center mb-2 group-hover:scale-105 transition-transform`}>
                  <module.icon className="h-6 w-6" />
                </div>
                <span className="text-xs font-medium text-center text-foreground leading-tight">
                  {module.title}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid gap-6 lg:grid-cols-5">
          {/* Recent Transactions - Takes 3 columns */}
          <Card className="lg:col-span-3">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-base font-semibold">{t('recent_transactions')}</CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/sales')}
                className="text-primary gap-1"
              >
                {t('view_all')}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="pb-4">
              {isLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-14 w-full" />
                  ))}
                </div>
              ) : recentTransactions.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  <Receipt className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p>{t('no_data')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {recentTransactions.map((tx) => (
                    <div 
                      key={tx.id} 
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Receipt className="h-5 w-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-foreground truncate">{tx.customer}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {tx.service} • {format(new Date(tx.date), 'dd MMM, hh:mm a')}
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <p className="font-bold text-foreground">{formatCurrency(tx.amount)}</p>
                        {getPaymentStatusBadge(tx)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Mini Insights - Takes 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Sales vs Expenses Chart */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {t('sales_vs_expenses')} (7 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <ChartSkeleton height={180} />
                ) : (
                  <Suspense fallback={<ChartSkeleton height={180} />}>
                    <LazySalesExpensesChart data={chartData} formatCurrency={formatCurrency} />
                  </Suspense>
                )}
              </CardContent>
            </Card>

            {/* Category Breakdown */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Service Categories
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <ChartSkeleton height={140} />
                ) : (
                  <Suspense fallback={<ChartSkeleton height={140} />}>
                    <LazyCategoryPieChart data={categoryData} formatCurrency={formatCurrency} />
                  </Suspense>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Profile Analytics Section */}
        <VLEAnalyticsDashboard />
      </div>

      {/* Quick Action Modals */}
      <QuickSaleModal
        open={saleModalOpen}
        onOpenChange={setSaleModalOpen}
        onSuccess={handleRefresh}
        onInvoiceCreated={handleInvoiceCreated}
      />
      <QuickExpenseModal
        open={expenseModalOpen}
        onOpenChange={setExpenseModalOpen}
        onSuccess={handleRefresh}
      />
      <QuickReceivePaymentModal
        open={paymentModalOpen}
        onOpenChange={setPaymentModalOpen}
        onSuccess={handleRefresh}
      />
      <DailySummaryPopup
        open={summaryModalOpen}
        onOpenChange={setSummaryModalOpen}
      />
      <InvoicePreviewPopup
        open={invoicePreviewOpen}
        onOpenChange={setInvoicePreviewOpen}
        invoice={lastInvoice}
      />
      <AddKioskTransactionModal
        open={kioskModalOpen}
        onOpenChange={setKioskModalOpen}
        onSuccess={handleRefresh}
      />
    </AppLayout>
  );
}
